import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { TextField, Button, Container, Typography, Box, Paper, Grid, IconButton, useTheme, CircularProgress } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SecurityIcon from '@mui/icons-material/Security';
import EmailIcon from '@mui/icons-material/Email';
import TimerIcon from '@mui/icons-material/Timer';

const OtpVerification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { verifyOtp, isVerifyingOtp } = useAuth();
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const email = location.state?.email || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await verifyOtp(email, otp);
    } catch (err) {
      setError(err.response?.data?.message || 'OTP verification failed');
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.main}10 100%)`,
      py: { xs: 4, md: 8 },
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `radial-gradient(circle at 0% 0%, ${theme.palette.primary.main}08 0%, transparent 50%)`,
        zIndex: 0
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: '50%',
        right: '-10%',
        width: '40%',
        height: '40%',
        background: `radial-gradient(circle, ${theme.palette.primary.main}15 0%, transparent 70%)`,
        transform: 'translateY(-50%)',
        zIndex: 0
      }
    }}>
      {/* Navigation Buttons */}
      <Box sx={{ 
        position: 'absolute',
        top: 20,
        left: 20,
        zIndex: 10,
        display: 'flex',
        gap: 2
      }}>
        <IconButton 
          onClick={() => navigate('/')}
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': { 
              bgcolor: 'white',
              transform: 'scale(1.1)'
            },
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            transition: 'all 0.3s'
          }}
        >
          <HomeIcon sx={{ color: theme.palette.primary.main }} />
        </IconButton>
        <IconButton 
          onClick={() => navigate(-1)}
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': { 
              bgcolor: 'white',
              transform: 'scale(1.1)'
            },
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            transition: 'all 0.3s'
          }}
        >
          <ArrowBackIcon sx={{ color: theme.palette.primary.main }} />
        </IconButton>
      </Box>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center" justifyContent="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              textAlign: 'center',
              mb: { xs: 4, md: 0 }
            }}>
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 800,
                  color: theme.palette.text.primary,
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' },
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: `0 2px 10px ${theme.palette.primary.main}20`
                }}
              >
                Verify Your Email
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  mb: 4,
                  fontSize: { xs: '1rem', md: '1.25rem' }
                }}
              >
                We've sent a verification code to your email address
              </Typography>
              <Box sx={{ 
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 3,
                mb: 4
              }}>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: theme.palette.primary.main,
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: `0 4px 15px ${theme.palette.primary.main}15`,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: `0 8px 25px ${theme.palette.primary.main}25`
                  }
                }}>
                  <EmailIcon />
                  <Typography>Check Your Email</Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: theme.palette.primary.main,
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: `0 4px 15px ${theme.palette.primary.main}15`,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: `0 8px 25px ${theme.palette.primary.main}25`
                  }
                }}>
                  <TimerIcon />
                  <Typography>Code Expires in 5 min</Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: theme.palette.primary.main,
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  boxShadow: `0 4px 15px ${theme.palette.primary.main}15`,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: `0 8px 25px ${theme.palette.primary.main}25`
                  }
                }}>
                  <SecurityIcon />
                  <Typography>Secure Verification</Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: { xs: 3, md: 4 },
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: `0 8px 25px ${theme.palette.primary.main}20`
                }
              }}
            >
              <Typography 
                component="h1" 
                variant="h4" 
                sx={{ 
                  textAlign: 'center',
                  mb: 3,
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -10,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '50px',
                    height: '3px',
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    borderRadius: '2px'
                  }
                }}
              >
                Enter OTP
              </Typography>
              <Typography 
                sx={{ 
                  textAlign: 'center',
                  mb: 3,
                  color: theme.palette.text.secondary
                }}
              >
                We've sent a 6-digit OTP to <strong>{email}</strong>
              </Typography>
              {error && (
                <Typography 
                  color="error" 
                  sx={{ 
                    textAlign: 'center',
                    mb: 2,
                    p: 1,
                    bgcolor: 'rgba(255, 0, 0, 0.1)',
                    borderRadius: '4px'
                  }}
                >
                  {error}
                </Typography>
              )}
              <Box 
                component="form" 
                onSubmit={handleSubmit} 
                sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                <TextField
                  fullWidth
                  label="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  inputProps={{ 
                    maxLength: 6,
                    style: { 
                      textAlign: 'center',
                      letterSpacing: '8px',
                      fontSize: '24px'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: theme.palette.primary.main
                    }
                  }}
                />
                <Button 
                  type="submit" 
                  fullWidth 
                  variant="contained" 
                  disabled={isVerifyingOtp}
                  sx={{ 
                    mt: 2,
                    py: 1.5,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    '&:hover': {
                      background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 15px ${theme.palette.primary.main}30`
                    },
                    '&:disabled': {
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                      opacity: 0.7
                    },
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    borderRadius: '8px',
                    transition: 'all 0.3s'
                  }}
                >
                  {isVerifyingOtp ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                      Verifying OTP...
                    </Box>
                  ) : (
                    'Verify OTP'
                  )}
                </Button>
                <Box sx={{ 
                  textAlign: 'center',
                  mt: 2
                }}>
                  <Typography 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      mb: 1
                    }}
                  >
                    Didn't receive the code?
                  </Typography>
                  <Button
                    variant="text"
                    sx={{ 
                      color: theme.palette.primary.main,
                      '&:hover': {
                        bgcolor: `${theme.palette.primary.main}10`
                      },
                      transition: 'all 0.3s'
                    }}
                  >
                    Resend OTP
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default OtpVerification;